import { Modal, InputNumber, Empty, Flex, Button } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAllWidgets } from "../../utils/formsHelper";

const ScoringPanel = ({ open, onClose, formElements = [], updateFieldOptions }) => {
    const { t } = useTranslation();

    // Flatten the (possibly nested) form tree and keep only option-based fields
    // that have scoring enabled.
    const scoringFields = getAllWidgets(formElements).filter(
        (field) => field.enableScoring && Array.isArray(field.options) && field.options.length > 0
    );

    // Group the scoring fields by their section, preserving first-seen order.
    // Fields that don't belong to a section are collected under an empty label
    // and rendered without a section heading.
    const sectionedFields = scoringFields.reduce((acc, field) => {
        const label = field.sectionLabel || "";
        let group = acc.find((entry) => entry.label === label);
        if (!group) {
            group = { label, fields: [] };
            acc.push(group);
        }
        group.fields.push(field);
        return acc;
    }, []);

    // Buffer the edited options locally, keyed by field id. Changes are only
    // pushed to the form when the user clicks Submit.
    const [draftOptions, setDraftOptions] = useState({});

    // (Re)initialise the buffer from the form whenever the panel is opened.
    useEffect(() => {
        if (open) {
            const initial = {};
            scoringFields.forEach((field) => {
                initial[field.id] = field.options.map((option) => ({ ...option }));
            });
            setDraftOptions(initial);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleScoreChange = (fieldId, optionIndex, value) => {
        setDraftOptions((prev) => ({
            ...prev,
            [fieldId]: (prev[fieldId] || []).map((option, index) =>
                index === optionIndex ? { ...option, score: value } : option
            ),
        }));
    };

    const handleSubmit = () => {
        scoringFields.forEach((field) => {
            const nextOptions = draftOptions[field.id];
            if (nextOptions) {
                updateFieldOptions(field.id, nextOptions);
            }
        });
        onClose();
    };

    return (
        <Modal
            title={t("Scoring Panel")}
            open={open}
            onCancel={onClose}
            footer={null}
            width="80%"
            centered
            styles={{ body: { height: "calc(90vh - 50px)", display: "flex", flexDirection: "column" } }}
        >
            {scoringFields.length === 0 ? (
                <Empty description={t("No fields with scoring enabled")} />
            ) : (
                <>
                    <div style={{ flex: 1, overflowY: "auto", paddingRight: 8 }}>
                        <Flex vertical gap={32}>
                            {sectionedFields.map((section, sectionIndex) => (
                                <div key={section.label || `section-${sectionIndex}`}>
                                    {section.label && (
                                        <div
                                            className="text-color-primary text-weight-700 text-h3"
                                            style={{
                                                marginBottom: 16,
                                                padding: "10px 16px",
                                                background: "#EEF0FF",
                                                borderLeft: "4px solid #141BCB",
                                                borderRadius: 6,
                                            }}
                                        >
                                            {section.label}
                                        </div>
                                    )}
                                    <Flex vertical gap={24}>
                                        {section.fields.map((field) => (
                                            <div key={field.id}>
                                                <div className="text-color-primary text-weight-600 text-h4" style={{ marginBottom: 12 }}>
                                                    {field.label || t("Untitled field")}
                                                </div>
                                                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                                                    {field.options.map((option, optionIndex) => (
                                                        <div key={option.value || optionIndex}>
                                                            <div className="text-regular text-color-primary" style={{ marginBottom: 4 }}>
                                                                {option.label}
                                                            </div>
                                                            <InputNumber
                                                                style={{ width: "100%" }}
                                                                placeholder={t("Enter score")}
                                                                value={draftOptions[field.id]?.[optionIndex]?.score}
                                                                onChange={(value) => handleScoreChange(field.id, optionIndex, value)}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </Flex>
                                </div>
                            ))}
                        </Flex>
                    </div>
                    <Flex justify="flex-end" style={{ paddingTop: 16 }}>
                        <Button type="primary" className="submit-button" onClick={handleSubmit}>
                            {t("Submit")}
                        </Button>
                    </Flex>
                </>
            )}
        </Modal>
    );
};

export default ScoringPanel;
